import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, TrendingUp, TrendingDown, Minus, CheckCircle2, AlertTriangle } from "lucide-react";
import { Patients } from "@/lib/api";
import { roundScore, formatScore, scoreDelta } from "@/lib/scoreFormat";
import FeatureGate from "@/components/FeatureGate";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SEG_PT = { feet: "Pés", knees: "Joelhos", pelvis: "Bacia", spine: "Coluna", shoulders: "Ombros", cervical: "Cervical", scapula: "Omoplatas" };

function deltaColor(delta) {
  if (delta > 2) return { txt: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/30", icon: TrendingUp };
  if (delta < -2) return { txt: "text-rose-300", bg: "bg-rose-500/10", border: "border-rose-500/30", icon: TrendingDown };
  return { txt: "text-slate-400", bg: "bg-slate-800/40", border: "border-slate-700", icon: Minus };
}

function ScoreBar({ value }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  const col = pct >= 80 ? "#34d399" : pct >= 60 ? "#fbbf24" : "#f43f5e";
  return (
    <div className="relative h-1.5 rounded-full bg-slate-800 overflow-hidden">
      <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${pct}%`, background: col, boxShadow: `0 0 8px ${col}` }} />
    </div>
  );
}

export default function ComparePage() {
  const { id: patientId } = useParams();
  const [sp, setSp] = useSearchParams();
  const [assessments, setAssessments] = useState([]);
  const [patient, setPatient] = useState(null);
  const [data, setData] = useState(null);
  const aId = sp.get("a") || "";
  const bId = sp.get("b") || "";

  useEffect(() => {
    (async () => {
      try {
        const [p, list] = await Promise.all([Patients.get(patientId), Patients.assessments(patientId)]);
        setPatient(p);
        const withAnalysis = list.filter(a => a.analysis);
        setAssessments(withAnalysis);
        if ((!aId || !bId) && withAnalysis.length >= 2) {
          const newest = withAnalysis[0];
          const previous = withAnalysis[1];
          setSp({ a: previous.id, b: newest.id });
        }
      } catch { toast.error("Falha ao carregar avaliações"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  useEffect(() => {
    if (!aId || !bId) { setData(null); return; }
    (async () => {
      try { setData(await Patients.compare(patientId, aId, bId)); }
      catch { toast.error("Não foi possível comparar"); }
    })();
  }, [patientId, aId, bId]);

  const segDiffs = useMemo(() => {
    if (!data) return [];
    const segs = new Set([
      ...Object.keys(data.a?.analysis?.segments || {}),
      ...Object.keys(data.b?.analysis?.segments || {}),
    ]);
    return Array.from(segs).map(k => {
      const av = roundScore(data.a?.analysis?.segments?.[k]?.score);
      const bv = roundScore(data.b?.analysis?.segments?.[k]?.score);
      const delta = (av != null && bv != null) ? bv - av : null;
      return { key: k, a: av, b: bv, delta };
    });
  }, [data]);

  const patternsDiff = useMemo(() => {
    if (!data) return { resolved: [], persistent: [], new_: [] };
    // Apenas aceita strings não vazias — evita "·" pendurado quando pattern.name é null/undefined.
    const namesA = (data.a?.analysis?.patterns || [])
      .map(p => (typeof p === "string" ? p : (p?.name || "")))
      .map(s => (s || "").trim())
      .filter(Boolean);
    const namesB = (data.b?.analysis?.patterns || [])
      .map(p => (typeof p === "string" ? p : (p?.name || "")))
      .map(s => (s || "").trim())
      .filter(Boolean);
    const A = new Set(namesA);
    const B = new Set(namesB);
    return {
      resolved:   [...A].filter(n => !B.has(n)),
      persistent: [...A].filter(n =>  B.has(n)),
      new_:       [...B].filter(n => !A.has(n)),
    };
  }, [data]);

  if (!patient) return <div className="text-slate-500">A carregar…</div>;

  const globalDelta = data ? (scoreDelta(data.b?.analysis?.global_score, data.a?.analysis?.global_score) ?? 0) : 0;
  const gColor = deltaColor(globalDelta);

  return (
    <div className="max-w-6xl mx-auto space-y-8" data-testid="compare-page">
      <Link to={`/patients/${patientId}`} className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 text-sm">
        <ArrowLeft className="w-4 h-4" /> Voltar ao paciente
      </Link>

      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-semibold">Comparação de Progresso</div>
        <h1 className="font-display text-4xl text-slate-50 mt-2">{patient.name}</h1>
        <p className="text-slate-400 text-sm mt-1">Evolução entre duas avaliações — visualiza o progresso por segmento e padrão clínico.</p>
      </div>

      <FeatureGate feature="comparison">
      {assessments.length < 2 ? (
        <div className="rounded-2xl border border-dashed border-slate-800 p-10 text-center bg-slate-900/30">
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-400" strokeWidth={1.5} />
          <div className="mt-3 text-slate-300">Precisas de ≥ 2 avaliações analisadas para comparar</div>
          <div className="text-slate-500 text-sm">Cria uma nova avaliação para este paciente para começar a acompanhar o progresso.</div>
        </div>
      ) : (
        <>
          {/* Selectors */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">Avaliação anterior (A)</div>
              <Select value={aId} onValueChange={(v) => setSp({ a: v, b: bId })}>
                <SelectTrigger className="mt-2 bg-slate-900 border-slate-800 text-slate-50" data-testid="select-a">
                  <SelectValue placeholder="Escolher avaliação" />
                </SelectTrigger>
                <SelectContent className="bg-slate-950 border-slate-800 text-slate-100">
                  {assessments.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {new Date(a.created_at).toLocaleString("pt-PT")} · {formatScore(a.analysis?.global_score)}/100
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-2xl border border-sky-500/30 bg-sky-500/5 p-5 glow-sky">
              <div className="text-xs uppercase tracking-[0.2em] text-sky-300 font-semibold">Avaliação recente (B)</div>
              <Select value={bId} onValueChange={(v) => setSp({ a: aId, b: v })}>
                <SelectTrigger className="mt-2 bg-slate-900 border-slate-800 text-slate-50" data-testid="select-b">
                  <SelectValue placeholder="Escolher avaliação" />
                </SelectTrigger>
                <SelectContent className="bg-slate-950 border-slate-800 text-slate-100">
                  {assessments.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {new Date(a.created_at).toLocaleString("pt-PT")} · {formatScore(a.analysis?.global_score)}/100
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {data && (
            <>
              {/* Global delta */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Pontuação Funcional A</div>
                  <div className="font-display text-4xl text-slate-50 mt-1">{formatScore(data.a?.analysis?.global_score)}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{data.a?.analysis?.classification}</div>
                  {data.a?.vas_score != null && (
                    <div className="mt-3 text-[11px] text-rose-300">VAS: <span className="font-mono">{data.a.vas_score}/10</span></div>
                  )}
                </div>
                <div className={`rounded-2xl border ${gColor.border} ${gColor.bg} p-6 text-center`} data-testid="global-delta">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Evolução</div>
                  <div className={`flex items-center justify-center gap-2 mt-1 font-display text-4xl ${gColor.txt}`}>
                    <gColor.icon className="w-7 h-7" strokeWidth={1.8} />
                    {globalDelta > 0 ? "+" : ""}{globalDelta}
                  </div>
                  <div className="text-xs text-slate-400 mt-1">pontos</div>
                  {data.a?.vas_score != null && data.b?.vas_score != null && (
                    <div className="mt-3 text-[11px] text-slate-400">
                      Δ VAS: <span className={data.b.vas_score < data.a.vas_score ? "text-emerald-300" : data.b.vas_score > data.a.vas_score ? "text-rose-300" : "text-slate-400"}>
                        {data.b.vas_score - data.a.vas_score > 0 ? "+" : ""}{data.b.vas_score - data.a.vas_score}
                      </span>
                    </div>
                  )}
                </div>
                <div className="rounded-2xl border border-sky-500/30 bg-sky-500/5 p-6 glow-sky">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-sky-300">Pontuação Funcional B</div>
                  <div className="font-display text-4xl text-slate-50 mt-1">{formatScore(data.b?.analysis?.global_score)}</div>
                  <div className="text-xs text-sky-300 mt-0.5">{data.b?.analysis?.classification}</div>
                  {data.b?.vas_score != null && (
                    <div className="mt-3 text-[11px] text-rose-300">VAS: <span className="font-mono">{data.b.vas_score}/10</span></div>
                  )}
                </div>
              </div>

              {/* Segment deltas */}
              <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
                <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-semibold">Evolução por segmento</div>
                <div className="mt-4 space-y-3">
                  {segDiffs.map(s => {
                    const c = s.delta == null ? deltaColor(0) : deltaColor(s.delta);
                    return (
                      <div key={s.key} className="grid grid-cols-[7rem_1fr_4rem_1fr_4rem] md:grid-cols-[9rem_1fr_5rem_1fr_5rem] items-center gap-3 text-sm">
                        <div className="text-slate-300">{SEG_PT[s.key] || s.key}</div>
                        <div><ScoreBar value={s.a} /></div>
                        <div className="text-right text-slate-400 font-mono text-xs">{s.a ?? "—"}</div>
                        <div><ScoreBar value={s.b} /></div>
                        <div className={`text-right font-mono text-xs inline-flex items-center justify-end gap-1 ${c.txt}`}>
                          <c.icon className="w-3 h-3" /> {s.delta == null ? "—" : (s.delta > 0 ? "+" : "") + s.delta}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Patterns diff */}
              <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5">
                  <div className="text-xs uppercase tracking-[0.2em] text-emerald-300 font-semibold flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> Resolvidos
                  </div>
                  {patternsDiff.resolved.length ? (
                    <ul className="mt-2 text-sm text-slate-200 space-y-1">{patternsDiff.resolved.map((n,i)=><li key={i}>· {n}</li>)}</ul>
                  ) : <div className="text-xs text-slate-500 mt-2">Nenhum padrão resolvido.</div>}
                </div>
                <div className="rounded-2xl border border-slate-700 bg-slate-800/30 p-5">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-300 font-semibold">Persistentes</div>
                  {patternsDiff.persistent.length ? (
                    <ul className="mt-2 text-sm text-slate-200 space-y-1">{patternsDiff.persistent.map((n,i)=><li key={i}>· {n}</li>)}</ul>
                  ) : <div className="text-xs text-slate-500 mt-2">Nenhum padrão persistente.</div>}
                </div>
                <div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-5">
                  <div className="text-xs uppercase tracking-[0.2em] text-rose-300 font-semibold flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Novos
                  </div>
                  {patternsDiff.new_.length ? (
                    <ul className="mt-2 text-sm text-slate-200 space-y-1">{patternsDiff.new_.map((n,i)=><li key={i}>· {n}</li>)}</ul>
                  ) : <div className="text-xs text-slate-500 mt-2">Nenhum padrão novo.</div>}
                </div>
              </section>
            </>
          )}
        </>
      )}
      </FeatureGate>
    </div>
  );
}
